import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import moment from 'moment';
import { DateObject } from 'react-multi-date-picker';
import mimeDb from 'mime-db';
import { intlShape } from '../components/base/IntlProviderWrapper';
import notificationDecorator from '../modules/Notifications/notificationDecorator';
import {
  CHECK_STATUS_NAMES, DEFAULT_ITEMS_OF_PAGE, STATUS_NAMES,
} from '../configs';
import { required } from './validation';
import getDefaultSort from './getDefaultSort';

const regMarkRegex = [
  {
    type: 'default',
    regex: [/\w{1}\s*\d{3}\s*\w{2}/, /\w{2}\s*\d{4}/, /\d{4}\s*\w{2}/],
  },
  {
    type: 'taxi',
    regex: [/\w{2}\s*\d{3}/],
  },
  {
    type: 'military',
    regex: [/\d{4}\s*\w{2}/, /\w{2}\s*\d{4}/, /\d{4}\s*\w{2}/],
  },
  {
    type: 'diplomat',
    regex: [/\d{3}\s*\w{2}\s*\d{1}/, /\d{3}\s*\w{1}\s*\d{3}/, /\w{1}\s*\d{5}/],
  },
  {
    type: 'police',
    regex: [/\d{1}\s*\w{4}/, /\d{3}\s*\w{1}/, /\d{4}\s*\w{1}/],
  },
];

export const getResponseError = (response, defaultMessage = '') => {
  const {
    formatMessage,
  } = intlShape;
  if (response && response.error) {
    if (typeof response.error === 'string') {
      return response.error;
    }
    let { message = '' } = response.error;
    if (response.error.code && !message) {
      message = formatMessage({ id: `error.code.${response.error.code}`, defaultMessage: ' ' }).trim();
    }

    return message || defaultMessage || formatMessage({ id: 'error.default' });
  }

  return response?.message || defaultMessage || formatMessage({ id: 'error.default' });
};

export const setFormError = (response, setError = () => {}) => {
  if (!response || !response.errors) {
    return;
  }
  const { errors } = response;
  const keys = Object.keys(errors);
  for (let i = 0, len = keys.length; i < len; i++) {
    const key = keys[i];
    setError(key, { type: 'server', message: errors[key].join(', ') }, { shouldFocus: !i });
  }
};

export const getFirstFormError = (errors = {}, prevName = []) => {
  const keys = Object.keys(errors);
  if (!keys || !keys.length) return null;
  const firstElement = errors[keys[0]];
  if (firstElement && firstElement.type && firstElement.message && firstElement.ref) {
    firstElement.name = [...prevName, keys[0]].join('.');
    return firstElement;
  }
  return getFirstFormError(firstElement, [...prevName, keys[0]]);
};

export const parserRegNumber = (regNumber = '') => regMarkRegex.find(reg => !!regNumber.match(reg.regex));

export const isObject = object => object != null && typeof object === 'object';

export const deepEqual = (object1, object2) => {
  const keys1 = Object.keys(object1);
  const keys2 = Object.keys(object2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    const val1 = object1[key];
    const val2 = object2[key];
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !deepEqual(val1, val2))
      || (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  return true;
};

export const getFilterValue = (filterData, name, defaultValue = '') => (filterData[name]
  && filterData[name].value
  ? filterData[name].value
  : defaultValue);

export const isClear = obj => Object.values(obj).reduce((acc, cur) => {
  const value = cur;
  if (isObject(value)) {
    return acc && isClear(value);
  } if (Array.isArray(value)) {
    return acc && !value.length;
  }
  return acc && !value;
}, true);

export const fallbackCopyTextToClipboard = text => {
  const textArea = document.createElement('textarea');
  textArea.value = text;

  textArea.style.top = '0';
  textArea.style.left = '0';
  textArea.style.position = 'fixed';

  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();

  try {
    const successful = document.execCommand('copy');
    if (!successful) {
      window.console.error('Fallback: Oops, unable to copy');
    }
  } catch (err) {
    window.console.error('Fallback: Oops, unable to copy', err);
  }
  document.body.removeChild(textArea);
};

export const copyTextToClipboard = text => {
  if (!navigator.clipboard) {
    fallbackCopyTextToClipboard(text);
    return;
  }
  navigator.clipboard.writeText(text).then(() => {}, err => {
    window.console.error('Async: Could not copy text: ', err);
  });
};

export const getPathNameWithOutParameters = (path = '') => {
  if (!path) {
    return path;
  }
  return path.split(':')[0];
};

export const addZero = (len = 1, part = '') => {
  const arrayLen = len - String(part).length >= 0 ? len - String(part).length : 0;
  return (
    Array(arrayLen)
      .fill('0')
      .join('') + String(part)
  );
};

export const responseLog = response => window.console.error(response);
export const responseLogWithNotification = (response, title = '', message = '') => {
  const {
    formatMessage,
  } = intlShape;
  window.console.error(response);
  notificationDecorator({
    title: title || formatMessage({ id: 'load.data' }),
    message: getResponseError(response, message) || formatMessage({ id: 'failed' }),
    isError: true,
  });
};
export const getHostMetrics = (host, needMetrics = []) => {
  const { metrics = [] } = host;
  return metrics.map(m => ({ metric_key: m.metric_key, value: m.value })).filter(m => !needMetrics || !needMetrics.length || needMetrics.includes(m.metric_key));
};
export const getHostMetricsByKeys = (host, needMetrics = []) => {
  const metrics = getHostMetrics(host, needMetrics);
  return metrics.reduce((acc, current) => {
    acc[current.metric_key] = current.value;
    return acc;
  }, {});
};
export const getPaginationOpts = (listMeta = {}, opts = {}) => {
  const page = opts && !Number.isNaN(Number(opts.page))
    ? Number(opts.page)
    : listMeta && listMeta.page && !!Number(listMeta.page)
      ? Number(listMeta.page)
      : 1;
  const limit = opts && opts.limit && !!Number(opts.limit)
    ? Number(opts.limit)
    : listMeta && listMeta.limit && !!Number(listMeta.limit)
      ? Number(listMeta.limit)
      : DEFAULT_ITEMS_OF_PAGE;
  return {
    page,
    limit,
  };
};

export const getJSONFromString = value => {
  if (!value) {
    return null;
  }
  try {
    return JSON.parse(value);
  } catch (e) {
    return null;
  }
};
export const saveFilter = (value, name) => {
  if (name) {
    window.localStorage.setItem(name, JSON.stringify(value));
  }
};
export const getSavedFilter = name => {
  if (name) {
    return getJSONFromString(window.localStorage.getItem(name)) || [];
  }
};
export const useFilterData = (initial = {}, callback = () => {}, name = '', useInitial = '') => {
  let savedValue = {};
  if (name) {
    savedValue = getJSONFromString(window.localStorage.getItem(name)) || {};
    if (savedValue && savedValue.date) {
      savedValue.date = savedValue.date.map(i => new DateObject({ date: moment(i).toDate() }));
    }
  }
  const [filter, setFilter] = useState(useInitial ? { ...initial } : { ...savedValue, ...initial });
  const saveFilter = value => {
    setFilter(value);
    if (name) {
      window.localStorage.setItem(name, JSON.stringify(value));
    }
  };
  const handleChangeFilter = (name, type = null, removeFalse = false) => event => {
    const filterData = { ...filter };
    const { value } = event.target;
    if (type) {
      filterData[name] = type(value);
    } else {
      filterData[name] = value;
    }
    if (value === null || (typeof value === 'string' && !value) || (removeFalse && !value)) {
      delete filterData[name];
    }
    saveFilter({ ...filterData });
    setTimeout(() => {
      callback({ filterData });
    }, 100);
  };
  const setFilterValue = filterData => {
    saveFilter({ ...filterData });
    setTimeout(() => {
      callback({ filterData });
    }, 100);
  };
  const clearFilter = (inputs = []) => {
    let filterData = { ...filter };
    if (inputs && inputs.length) {
      for (let i = 0, len = inputs.length; i < len; i++) {
        const name = inputs[i];
        if (name && filterData && filterData[name]) {
          delete filterData[name];
        }
      }
    } else {
      filterData = {};
    }
    saveFilter(filterData);
    setTimeout(() => {
      callback({ filterData });
    }, 100);
  };
  const getFilterValue = name => (filter && typeof filter[name] !== 'undefined' ? filter[name] : '');
  return {
    handleChangeFilter, filter, getFilterValue, clearFilter, setFilterValue,
  };
};
export const useSortData = (initial = '', callback = () => {}, name = '') => {
  let savedValue = [];
  if (name) {
    savedValue = getJSONFromString(window.localStorage.getItem(name)) || [];
  }
  const [sort, setSort] = useState([...initial, ...savedValue]);
  const saveSort = value => {
    setSort(value);
    if (name) {
      window.localStorage.setItem(name, JSON.stringify(value));
    }
  };
  const changeSort = name => () => {
    const sortData = [...sort];
    const index = sortData.findIndex(i => i === name || i === `-${name}`);
    if (index >= 0) {
      if (sortData[index] === name) {
        sortData[index] = `-${name}`;
      } else {
        sortData.splice(index, 1);
      }
    } else {
      sortData.push(name);
    }

    saveSort([...sortData]);
    setTimeout(() => {
      callback({ sortData });
    }, 100);
  };

  return { changeSort, sort };
};

export const filterByName = value => item => String(item.name).toLowerCase().includes(String(value).toLowerCase());

export class PermissionDeniedError extends Error {
  constructor(message, options) {
    // Need to pass `options` as the second parameter to install the "cause" property.
    super(message, options);
    this.status = 403;
  }
}

export const RegNumEnToRu = text => {
  if (!text) {
    return text;
  }
  text = text
    .replace(/A/g, 'А')
    .replace(/B/g, 'В')
    .replace(/E/g, 'Е')
    .replace(/K/g, 'К')
    .replace(/M/g, 'М')
    .replace(/H/g, 'Н')
    .replace(/O/g, 'О')
    .replace(/P/g, 'Р')
    .replace(/C/g, 'С')
    .replace(/T/g, 'Т')
    .replace(/Y/g, 'У')
    .replace(/X/g, 'Х');
  return text;
};

export const RegNumRuToEn = text => {
  if (!text) {
    return text;
  }
  text = text
    .replace(/А/g, 'A')
    .replace(/В/g, 'B')
    .replace(/Е/g, 'E')
    .replace(/К/g, 'K')
    .replace(/М/g, 'M')
    .replace(/Н/g, 'H')
    .replace(/О/g, 'O')
    .replace(/Р/g, 'P')
    .replace(/С/g, 'C')
    .replace(/Т/g, 'T')
    .replace(/У/g, 'Y')
    .replace(/Х/g, 'X');
  return text;
};
// get filter data from storage
export function getFilterData(name, defaultValue = null, adds = null) {
  const res = getJsonFromStorage(name, defaultValue);
  if (adds && typeof adds === 'object') {
    const keys = Object.keys(adds);
    for (let i = 0, len = keys.length; i < len; i++) {
      if (typeof adds[keys[i]] !== 'undefined') {
        res[keys[i]] = adds[keys[i]];
      }
    }
  }
  return res;
}

export function getJsonFromStorage(name, defaultValue = null) {
  const value = localStorage.getItem(name);
  if (value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return defaultValue;
    }
  }
  return defaultValue;
}

export const useStoreState = function useStoreState(initialState, name) {
  const [value, setter] = useState(getJsonFromStorage(name) || initialState);
  const setValue = value => {
    setter(value);
  };
  const save = value => {
    localStorage.setItem(name, JSON.stringify(value));
  };
  const reset = () => setValue(getJsonFromStorage(name) || initialState);
  return [value, setValue, reset, save];
};

window.updateTimers = [];

export const useUpdatePeriod = (
  callback = Promise.resolve,
  updatePeriod = 0,
  noPromise = false,
) => {
  const timerRef = useRef(null);
  const periodRef = useRef(updatePeriod);
  const stoppedRef = useRef(false);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    periodRef.current = updatePeriod ?? 0;
  }, [updatePeriod]);

  const clear = useCallback(() => {
    if (!timerRef.current) {
      return;
    }

    const index = window.updateTimers.findIndex(
      i => i === timerRef.current,
    );

    if (index > -1) {
      window.updateTimers.splice(index, 1);
    }

    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  const createTimer = useCallback(() => {
    timerRef.current = setTimeout(load, periodRef.current * 1000);
    window.updateTimers.push(timerRef.current);
  }, []);

  const load = useCallback(() => {
    clear();

    const result = callbackRef.current();

    if (noPromise) {
      if (periodRef.current && !stoppedRef.current) {
        createTimer();
      }
      return;
    }

    Promise.resolve(result).finally(() => {
      if (periodRef.current && !stoppedRef.current) {
        createTimer();
      }
    });
  }, [clear, createTimer, noPromise]);

  const destroy = useCallback(() => {
    stoppedRef.current = true;
    clear();
  }, [clear]);

  const setPeriod = useCallback(
    value => {
      stoppedRef.current = false;
      periodRef.current = value ?? 0;
      load();
      return destroy;
    },
    [load, destroy],
  );

  useEffect(() => destroy, [destroy]);

  return [setPeriod, destroy, clear, load, createTimer];
};

export const useSort = function useSort(columns = []) {
  const [sort, setSort] = useState(getDefaultSort(columns));

  const changeSort = fieldName => {
    if (sort && (sort === fieldName || sort === `-${fieldName}`)) {
      if (sort === `-${fieldName}`) {
        setSort(fieldName);
      } else if (sort === fieldName) {
        setSort('');
      }
    } else {
      setSort(`-${fieldName}`);
    }
  };
  return [sort, changeSort];
};

export const onlyNumbersOption = {
  valueAsNumber: true,
  pattern: {
    value: /^(0|[1-9]\d*)(\.\d+)?$/,
  },
};

export const round = function round(number) {
  return Math.round(number * 1000) / 1000;
};

export const nameBySave = (text = '') => {
  const res = text.replace(/[^a-zа-яё0-9 ._№\-\s]/gi, '_');
  return res.replace(/^[^a-zа-яё0-9\s]*/gi, '');
};

export const clearEmptyProp = (filter = {}) => Object.keys(filter).reduce((acc, key) => {
  if (filter[key]) {
    acc[key] = filter[key];
  }
  return acc;
}, {});

export const getMomentValue = (value, format) => {
  let v = moment(value, format);
  if (!v || !v.isValid()) {
    v = moment();
  }
  return v;
};

export const getPTZ = data => ({
  pan: data && data.pan ? round(Number(data.pan)) : 0,
  tilt: data && data.tilt ? round(Number(data.tilt)) : 0,
  zoom: data && data.zoom ? round(Number(data.zoom)) : 0,
});

const degreesConvert = ({ deg = 0, min = 0, sec = 0 }) => {
  let newDeg = deg;
  let newMin = min;
  let newSec = sec;
  if (newSec >= 60) { newMin += Math.floor(newSec / 60); newSec %= 60; }
  if (newMin >= 60) { newDeg += Math.floor(newMin / 60); newMin %= 60; }
  return Math.round((newDeg * 1 + newMin / 60 + newSec / 3600) * 1000000) / 1000000;
};

const coordinateConvert = coordinate => {
  const newCoordinate = parseFloat(coordinate);
  const deg = Math.floor(newCoordinate);
  const minWithSec = (newCoordinate - deg) * 60;
  const min = Math.floor(minWithSec);
  const sec = Math.round((minWithSec - min) * 60);
  return { deg, min, sec };
};

export const toLinear = ({ longitude, latitude }) => ({ longitude: degreesConvert(longitude), latitude: degreesConvert(latitude) });

export const toDMS = (LN, LA) => ({ longitude: coordinateConvert(LN), latitude: coordinateConvert(LA) });

export const getURLParams = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const data = {};
  for (const key of searchParams.keys()) {
    data[key] = searchParams.get(key);
  }
  return data;
};

export const objectFlip = obj => {
  const ret = {};
  Object.keys(obj).forEach(key => {
    ret[obj[key]] = key;
  });
  return ret;
};

export const sizeFormatter = (size = 0) => {
  const { formatNumber } = intlShape;
  const k = 1024;
  const sizes = ['byte', 'kilobyte', 'megabyte', 'gigabyte', 'terabyte'];
  if (!size) {
    return formatNumber(0, { style: 'unit', unit: 'byte' });
  }
  const i = Math.floor(Math.log(size) / Math.log(k));
  const shortSize = parseFloat((size / k ** i).toFixed(2));
  return formatNumber(shortSize, { style: 'unit', unit: sizes[i] });
};

export const getExt = fileMime => {
  const data = mimeDb[fileMime];
  if (!data) return '';
  const exts = data.extensions.filter(f => f.length <= 3);
  const exts4 = data.extensions.filter(f => f.length <= 4);
  if (!exts || !exts.length) return exts4.length ? exts4[0] : '';
  return exts[0];
};

export const getDeepValue = (obj = {}, path = '') => {
  const pathParts = path.split('.');
  let newValue = { ...obj };
  for (let i = 0; i < pathParts.length; i++) {
    if (newValue[pathParts[i]] === undefined || newValue[pathParts[i]] === null) {
      return undefined;
    }
    newValue = newValue[pathParts[i]];
  }
  return newValue;
};

export const getKeyByValue = (object, value) => Object.keys(object).find(key => object[key] === value);

export const getInputPropsWithSearch = (getValues, searchParams, setSearchParams, name, defaultValue) => ({
  value: getValues(name) || defaultValue,
  onChange: e => {
    const v = e.target.value;
    if (searchParams.has(name) && (!v || !v.length)) {
      searchParams.delete(name);
      setSearchParams(searchParams);
    } else {
      setSearchParams({ [name]: JSON.stringify(v) || defaultValue });
    }
  },
});

export const calculatedViewMode = mode => {
  if (mode === 1) {
    return {
      with_trashed: true,
      with_archived: true,
    };
  }
  if (mode === 3) {
    return {
      only_archived: true,
    };
  }
  if (mode === 4) {
    return {
      only_trashed: true,
    };
  }
  return {};
};

export const replaceQuotes = text => {
  const result = String(text)
    .replaceAll('„', ' «')
    .replaceAll('“', '»')
    .replaceAll(' “', ' «')
    .replaceAll('”', '»')
    .replaceAll(/^"/ig, '«')
    .replaceAll('"', '»')
    .replaceAll('("', '«')
    .replaceAll(['\n»'], ['\n«'])
    .replaceAll([' »'], ' «')
    .replaceAll(['(»'], ['(«']);
  return result;
};

export const companyNameTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = replaceQuotes(String(v))
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ')
      .toUpperCase();
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const quotesTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = replaceQuotes(String(v))
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const toLowerCase = {
  output: e => e,
  input: v => String(v).toLowerCase(),
};

export const grzTransliterationTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;

    const enToRuMap = {
      A: 'А',
      B: 'В',
      C: 'С',
      E: 'Е',
      H: 'Н',
      K: 'К',
      M: 'М',
      O: 'О',
      P: 'Р',
      T: 'Т',
      X: 'Х',
      Y: 'У',
    };

    e.target.value = v
      .toUpperCase()
      .split('')
      .map(char => enToRuMap[char] || char)
      .join('');

    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const addressTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    e.target.value = String(v)
      .replace(/^\s+/ig, '')
      .replace(/\s+/ig, ' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const nameTransform = {
  output: e => {
    let caretPos = e.currentTarget.selectionStart;
    const v = e.target.value;
    const len = v.length;
    const exceptions = ['оглы'];
    e.target.value = String(v).toLowerCase()
      .split(' ')
      .map(w => {
        if (exceptions.includes(w)) { return w; }
        return w.charAt(0).toUpperCase() + w.slice(1);
      })
      .join(' ');
    caretPos -= (len - e.target.value.length);
    e.currentTarget.setSelectionRange(caretPos, caretPos);
    return e;
  },
  input: v => v,
};

export const getIsRequired = (dtoObject, customValidator) => (dtoObject
      && dtoObject.validators
      && dtoObject.validators.length
      && !!dtoObject.validators.find(validator => validator.toString() === required.toString()))
    || !!customValidator.find(validator => validator.toString() === required.toString());

export const createClearObject = fields => Object.keys(fields).reduce((acc, key) => {
  const field = fields[key];
  const isArray = Array.isArray(field);
  if (field && typeof field === 'object' && !isArray) {
    acc[key] = createClearObject(field);
  } else if (isArray) {
    acc[key] = [];
  } else {
    acc[key] = '';
  }
  return acc;
}, {});

// export const getFieldValidation = (meta = {}, fields) => {
//   const { checks = {}, errors = {} } = meta;
//   const result = {
//     checks: [],
//     errors: [],
//   };
//   let names = [fields];
//   if (Array.isArray(fields)) {
//     names = fields;
//   }
//   for (const n of names) {
//     if (checks && checks[n]) {
//       result.checks = [...result.checks, ...checks[n]];
//     }
//     if (errors && errors[n]) {
//       result.errors = [...result.errors, ...errors[n]];
//     }
//   }
//   return result;
// };

export const getFieldValidation = (formMeta, name) => {
  const fieldNames = Array.isArray(name) ? name : [name];
  const firstFieldName = fieldNames[0];
  const fieldChecks = formMeta.checks?.[firstFieldName];

  const allValidateErrors = [];
  fieldNames.forEach(fieldName => {
    const fieldErrors = formMeta.errors?.[fieldName];
    if (Array.isArray(fieldErrors) && fieldErrors?.length) {
      // TODO это для поддержки совместимости. Удалить после полного перехода на новый формат
      allValidateErrors.push(...fieldErrors);
    }
    if (Array.isArray(fieldErrors?.messages) && fieldErrors?.messages?.length) {
      allValidateErrors.push(...fieldErrors.messages);
    }
  });

  const checkStatus = fieldChecks
    ? CHECK_STATUS_NAMES[fieldChecks.status]
    : '';

  const checkErrors = fieldChecks?.errors
    ? fieldChecks.errors.map(error => error?.message).join(', ')
    : 'Проверка не задана.';

  const validateStatus = allValidateErrors.length
    ? STATUS_NAMES.DENIED
    : STATUS_NAMES.SUCCESS;

  const validateErrors = allValidateErrors.length
    ? allValidateErrors.join(', ')
    : 'Нет информации.';

  return {
    checkStatus,
    checkErrors,
    validateStatus,
    validateErrors,
  };
};

export const setValueByPath = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  // eslint-disable-next-line no-return-assign
  const target = keys.reduce((acc, key) => acc[key] = { ...acc[key] }, obj);

  if (lastKey) {
    target[lastKey] = value;
    return;
  }
  Object.assign(target, value);
};

export const getExistFields = obj => Object.entries(obj).reduce((acc, [key, value]) => {
  if (value) {
    acc[key] = value;
  }
  return acc;
}, {});

export const detectExt = base => new Promise((resolve, reject) => {
  const i = new XMLHttpRequest();
  i.onload = resolve;
  i.onerror = reject;
  i.open('GET', `chrome-extension://${base}/views/app.html`);
  i.send();
});

export const getKeyPopUp = (key, id) => `${key}:${id}`;

export const parseValueToArray = (v, multiple) => {
  const isString = typeof v === 'string';
  const isArray = Array.isArray(v);
  if (!v || (!isString && !isArray)) {
    return [];
  }
  if (isString) {
    if (multiple) {
      return String(v).split(',').map(i => i.trim());
    }
    return [v];
  }
  return v;
};

export const setApplicant = (applicant, setValue) => {
  if (!applicant) {
    return;
  }
  const fields = [
    'federal_law_number',
    'legal_entity_type_label',
    'identity_type_label',
    'gender_label',
    'registration_type_label',
    'citizenship_label',
    'legal_entity_type',
    'legal_entity_form',
    'full_name',
    'short_name',
    'ogrn',
    'inn',
    'identity_type',
    'identity_series',
    'identity_number',
    'identity_issue_date',
    'identity_expiration_date',
    'identity_issuer',
    'last_name',
    'first_name',
    'middle_name',
    'phone',
    'email',
    'legal_address',
    'address',
    'office_address',
    'birth_date',
    'place_of_birth',
    'gender',
    'registration_type',
    'snils',
    'citizenship',
    'commercial_name',
    'registration_address',
    'office_phone',
    'office_email',
    'logo_files',
    'isMatch',
    'applicant_id',
    'applicant_status',
    'fact_address',
  ];

  setFields(applicant, 'applicant.', fields, setValue);
};

export const setRepresentative = (representative, setValue) => {
  if (!representative) {
    return;
  }
  const fields = [
    'last_name',
    'first_name',
    'middle_name',
    'organization_name',
    'inn',
    'address',
    'phone',
    'email',
    'document_of_authority_number',
    'document_of_authority_issue_date',
    'document_of_authority_expiration_date',
  ];

  setFields(representative, 'representative.', fields, setValue);
};

export const setFields = (data, prefix, fieldsNames, setValue) => {
  if (!fieldsNames?.length || !data) {
    return;
  }
  for (const name of fieldsNames) {
    if (data?.[name]) {
      setValue(`${prefix}${name}`, data?.[name]);
    }
  }
};

export const buildAddress = address => {
  const parts = [
    address.index || '',
    address.country || '',
    address.region || '',
    address.area || '',
    address.city || '',
    address.place || '',
    address.location || '',
    address.street || '',
    `${address?.house || address.vladenie || ''}${address.letter || ''}${address.slash || ''}`.trim(),
    address.external || '',
    address.building || '',
    address.room || '',
  ].filter(Boolean);

  return parts.join(', ');
};

export const removeNulls = obj => {
  if (obj) {
    Object.keys(obj).forEach(key => {
      if (obj[key] === null) {
        delete obj[key];
      }
    });
  }
};

export const propsToRow = props => {
  if (props && Array.isArray(props)) {
    return props.join(', ');
  }
  if (props && typeof props === 'object') {
    return Object.values(props).join(', ');
  }
  return props;
};

export const isDoc = mime => {
  const allowedTypes = [
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  return allowedTypes.includes(mime);
};
